/**
 * Присутствие артикула на площадках для окон сущностей.
 *
 * Один и тот же расчёт нужен окну товара (цепочка, «Обзор», «Площадки») и окну
 * черновика ПИМ (переключатель площадок в цепочке): запрос присутствия — это
 * GET /products/:article/presence, а правила «куда вести клик» живут здесь, в
 * одном месте. Звено площадки без карточки не прячется, а остаётся неактивным
 * с подсказкой: по нему видно, где артикула нет.
 */

import { useCallback, useEffect, useState } from 'react'
import type { ChainMarketplaces, ChainMenuItem, ChainTone } from '../../components/ui/EntityChain'
import type { EntityTarget } from '../../components/entity-view/navigatorContext'
import { productsV2API } from './api'
import { PRESENCE_STATE_INFO } from './lib'
import type { PresenceMarketplace, PresenceState, ProductPresenceEntry } from './types'

export const PRESENCE_MARKETPLACES: PresenceMarketplace[] = ['wb', 'ozon', 'ym', 'site']

/** Короткие подписи переключателя: ВБ · ОЗ · ЯМ · Сайт. */
export const PRESENCE_CHAIN_LABEL: Record<PresenceMarketplace, string> = {
  wb: 'ВБ',
  ozon: 'ОЗ',
  ym: 'ЯМ',
  site: 'Сайт',
}

export const NO_CARD_TITLE = 'Нет карточки'

/** Состояние присутствия → цвет точки звена. */
export function presenceTone(state: PresenceState | undefined): ChainTone {
  switch (state) {
    case 'live':
      return 'ok'
    case 'draft':
    case 'publishing':
      return 'info'
    case 'error':
      return 'bad'
    case 'excluded':
      return 'warn'
    default:
      return 'none'
  }
}

/** Порядок важности тонов для сводной точки площадки с несколькими кабинетами. */
const TONE_RANK: Record<ChainTone, number> = { bad: 4, ok: 3, info: 2, warn: 1, none: 0 }

export function worstTone(tones: ChainTone[]): ChainTone {
  return tones.reduce<ChainTone>((best, tone) => (TONE_RANK[tone] > TONE_RANK[best] ? tone : best), 'none')
}

export function presenceEntryTone(entry: ProductPresenceEntry): ChainTone {
  return entry.channel_state === 'error' || Boolean(entry.errors?.length) ? 'bad' : presenceTone(entry.state)
}

export function presenceEntryLabel(entry: ProductPresenceEntry): string | undefined {
  const label = PRESENCE_STATE_INFO[entry.state]?.label
  return entry.state === 'live' && (entry.channel_state === 'error' || entry.errors?.length)
    ? `${label}; есть ошибки отправки или модерации` : label
}

export interface PresenceData {
  entries: ProductPresenceEntry[] | null
  error: string | null
  reload: () => void
}

/**
 * Загрузка присутствия артикула. Ошибка не роняет окно: переключатель просто
 * остаётся без активных звеньев, а подробности видны на вкладке «Площадки».
 */
export function useProductPresence(article: string | null | undefined): PresenceData {
  const [entries, setEntries] = useState<ProductPresenceEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((value) => value + 1), [])

  useEffect(() => {
    if (!article) {
      setEntries(null)
      setError(null)
      return undefined
    }
    let cancelled = false
    setEntries(null)
    setError(null)
    Promise.resolve()
      .then(() => productsV2API.getPresence(article))
      .then((data) => {
        if (!cancelled) setEntries(Array.isArray(data?.entries) ? data.entries : [])
      })
      .catch((requestError: any) => {
        if (cancelled) return
        setError(
          requestError?.response?.data?.error?.message || requestError?.message || 'Не удалось загрузить карточки',
        )
      })
    return () => {
      cancelled = true
    }
  }, [article, tick])

  return { entries, error, reload }
}

/**
 * Куда ведёт запись присутствия: зеркало площадки → окно карточки витрины,
 * иначе черновик ПИМ → окно черновика, иначе никуда. Ключ карточки: ВБ — nmID,
 * ОЗОН и Я.Маркет — offer_id (он же артикул продавца), сайт — артикул.
 */
export function presenceTarget(entry: ProductPresenceEntry, article: string): EntityTarget | null {
  if (entry.marketplace === 'site') {
    return entry.state !== 'none' || entry.mirror || entry.pim_card
      ? { kind: 'mp_card', marketplace: 'site', key: article }
      : null
  }
  if (entry.mirror) {
    const key =
      entry.marketplace === 'wb'
        ? entry.mirror.external_id
        : entry.mirror.vendor_code || entry.mirror.external_id || article
    if (key) {
      return {
        kind: 'mp_card',
        marketplace: entry.marketplace,
        sellerId: entry.seller_id,
        key: String(key),
      }
    }
  }
  if (entry.pim_card && entry.seller_id != null) {
    return {
      kind: 'draft',
      cardId: entry.pim_card.id,
      sellerId: entry.seller_id,
      marketplace: entry.marketplace,
    }
  }
  return null
}

const cabinetLabel = (entry: ProductPresenceEntry): string =>
  entry.cabinet_code || entry.seller_short_name || entry.seller_name || (entry.seller_id != null ? `Кабинет ${entry.seller_id}` : 'Кабинет')

export interface PresenceChainOptions {
  entries: ProductPresenceEntry[] | null
  article: string
  /** Навигатор доступен: без него звенья неактивны (отдельные тесты, страницы без AppLayout). */
  open?: (target: EntityTarget) => void
  /** Окно показывает эту площадку: звено current, а в меню кабинетов отмечен текущий. */
  current?: { marketplace: PresenceMarketplace; sellerId?: number | null }
  /** Перед переходом спросить о несохранённых правках. */
  guard?: (action: () => void) => void
}

/** Звено цепочки «ВБ · ОЗ · ЯМ · Сайт». */
export function buildPresenceChain(options: PresenceChainOptions): ChainMarketplaces {
  const { entries, article, open, current, guard } = options
  const go = (target: EntityTarget) => {
    if (!open) return
    if (guard) guard(() => open(target))
    else open(target)
  }

  return {
    key: 'presence',
    kind: 'marketplaces',
    items: PRESENCE_MARKETPLACES.map((marketplace) => {
      const label = PRESENCE_CHAIN_LABEL[marketplace]
      const isCurrent = current?.marketplace === marketplace
      if (entries === null) {
        return {
          key: marketplace,
          label,
          tone: 'none' as const,
          current: isCurrent,
          disabled: !isCurrent,
          title: 'Загружаем присутствие на площадках',
        }
      }
      const group = entries.filter((entry) => entry.marketplace === marketplace)
      const tone = worstTone(group.map((entry) => presenceEntryTone(entry)))
      const openable = group.filter((entry) => presenceTarget(entry, article) !== null)

      const menu: ChainMenuItem[] | undefined =
        group.length > 1
          ? group.map((entry) => {
              const target = presenceTarget(entry, article)
              const isCurrentCabinet = isCurrent && entry.seller_id === (current?.sellerId ?? null)
              return {
                key: `${marketplace}:${entry.seller_id ?? ''}`,
                label: cabinetLabel(entry),
                sub: presenceEntryLabel(entry),
                tone: presenceEntryTone(entry),
                current: isCurrentCabinet,
                disabled: !target && !isCurrentCabinet,
                title: target || isCurrentCabinet ? undefined : NO_CARD_TITLE,
                onClick: target ? () => go(target) : undefined,
              }
            })
          : undefined

      if (group.length === 0 || openable.length === 0) {
        return {
          key: marketplace,
          label,
          tone,
          current: isCurrent,
          disabled: !isCurrent,
          title: isCurrent ? undefined : NO_CARD_TITLE,
        }
      }

      const single = group.length === 1 ? presenceTarget(group[0], article) : null
      const stateLabel = group.length === 1 ? presenceEntryLabel(group[0]) : undefined
      return {
        key: marketplace,
        label,
        tone,
        current: isCurrent,
        title: stateLabel ?? (menu ? 'Выбрать кабинет' : undefined),
        onClick: single && open ? () => go(single) : undefined,
        disabled: !open && !isCurrent,
        menu: open ? menu : undefined,
      }
    }),
  }
}

/** Сколько площадок из четырёх сейчас на витрине. */
export function livePlatformCount(entries: ProductPresenceEntry[] | null): number | null {
  if (entries === null) return null
  return PRESENCE_MARKETPLACES.filter((marketplace) =>
    entries.some((entry) => entry.marketplace === marketplace && entry.state === 'live'),
  ).length
}
